// AI Backend Client - HTTP REST API Client
import { AISignal } from './ai-signals-api';

// Type definitions for the backend API
export interface MarketData {
  symbol: string;
  current_price: number;
  price_change_24h?: number;
  volume_24h?: number;
  market_cap?: number;
  technical_indicators?: {
    rsi?: number;
    macd?: number;
    bollinger_bands?: {
      upper: number;
      middle: number;
      lower: number;
    };
    sma_20?: number;
    sma_50?: number;
    ema_12?: number;
    ema_26?: number;
  };
  volume_data?: number[];
  historical_prices?: number[];
}

export interface PerformanceMetrics {
  total_signals: number;
  success_rate: number;
  avg_confidence: number;
  total_return: number;
  max_drawdown: number;
  sharpe_ratio: number;
  win_rate: number;
  avg_holding_period: number;
  last_updated: string;
}

// Error types for typed error responses
export class NetworkError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class ClientError extends Error {
  constructor(message: string, public statusCode: number) {
    super(message);
    this.name = 'ClientError';
  }
}

export class ServerError extends Error {
  constructor(message: string, public statusCode: number) {
    super(message);
    this.name = 'ServerError';
  }
}

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export type APIError = NetworkError | ClientError | ServerError | TimeoutError;

class AIBackendClient {
  private baseUrl: string;
  private timeout: number;
  private maxRetries: number;
  private retryDelay: number;

  constructor() {
    // Enhanced production configuration with fallbacks
    this.baseUrl = this.getProductionUrl();
    this.timeout = 10000; // 10 seconds
    this.maxRetries = 3;
    this.retryDelay = 1000; // Start at 1 second
  }

  private getProductionUrl(): string {
    // Try production URL first
    const primaryUrl = import.meta.env.VITE_AI_BACKEND_URL;
    const fallbackUrl = import.meta.env.VITE_AI_BACKEND_FALLBACK_URL;
    
    // In production, try primary first, then fallback
    if (import.meta.env.PROD && primaryUrl) {
      console.log('[AI Backend] Using production primary URL:', primaryUrl);
      return primaryUrl + '/api';
    }
    
    if (fallbackUrl) {
      console.log('[AI Backend] Using fallback URL:', fallbackUrl);
      return fallbackUrl + '/api';
    }
    
    // Development fallback
    console.log('[AI Backend] Using development fallback URL');
    return 'http://localhost:8000/api';
  }

  private async tryFallbackEndpoint<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const fallbackUrl = import.meta.env.VITE_AI_BACKEND_FALLBACK_URL;
    if (!fallbackUrl) {
      throw new Error('No fallback URL configured');
    }
    
    const fallbackFullUrl = `${fallbackUrl}${endpoint}`;
    console.log('[AI Backend] Trying fallback endpoint:', fallbackFullUrl);
    
    const response = await fetch(fallbackFullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Fallback endpoint failed: ${response.status}`);
    }
    
    return await response.json();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      console.log(`[AI Backend] ${options.method || 'GET'} ${url}`);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      console.log(`[AI Backend] Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          throw new ClientError(`Client error: ${response.status} ${response.statusText}`, response.status);
        } else if (response.status >= 500) {
          throw new ServerError(`Server error: ${response.status} ${response.statusText}`, response.status);
        } else {
          throw new NetworkError(`Network error: ${response.status} ${response.statusText}`, response.status);
        }
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${this.timeout}ms`);
      }

      // Try fallback endpoint if primary fails (only once per request)
      if (retryCount === 0 && (error instanceof NetworkError || error instanceof ServerError || error instanceof TimeoutError)) {
        try {
          console.log('[AI Backend] Primary endpoint failed, trying fallback...');
          return await this.tryFallbackEndpoint<T>(endpoint, options);
        } catch (fallbackError) {
          console.error('[AI Backend] Fallback endpoint also failed:', fallbackError);
          // Continue with retry logic on primary endpoint
        }
      }

      // Retry logic for network errors and server errors
      if (
        retryCount < this.maxRetries &&
        (error instanceof NetworkError || error instanceof ServerError || error instanceof TimeoutError)
      ) {
        const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff
        console.log(`[AI Backend] Retrying in ${delay}ms (attempt ${retryCount + 1}/${this.maxRetries})`);

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  // Get latest signals with optional limit
  async getLatestSignals(limit?: number): Promise<AISignal[]> {
    const params = limit ? `?limit=${limit}` : '';
    return this.makeRequest<AISignal[]>(`/signals/latest${params}`);
  }

  // Get signals for a specific symbol with optional limit
  async getSignalsForSymbol(symbol: string, limit?: number): Promise<AISignal[]> {
    const params = limit ? `?limit=${limit}` : '';
    return this.makeRequest<AISignal[]>(`/signals/${symbol}${params}`);
  }

  // Get the latest signal for a specific symbol
  async getLatestSignalForSymbol(symbol: string): Promise<AISignal> {
    return this.makeRequest<AISignal>(`/signals/${symbol}/latest`);
  }

  // Get performance metrics
  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    return this.makeRequest<PerformanceMetrics>('/performance');
  }

  // Generate a new signal based on market data
  async generateSignal(marketData: MarketData): Promise<AISignal> {
    return this.makeRequest<AISignal>('/signals/generate', {
      method: 'POST',
      body: JSON.stringify(marketData),
    });
  }
}

// Export singleton instance
export const aiBackendClient = new AIBackendClient();